<template>
    <page-title
        title="年間計画をつくる"
        icon="bi-bricks"
        :title_sub_1="is_muji ? '無地' : ''"
        :backTo="{ name: is_muji ? 'PageRepocraftMujiHome' : 'PageRepocraftHome' }"
    >
    </page-title>

    <inline-loader v-if="loading"></inline-loader>

    <template v-else>
        <div class="d-flex flex-wrap gap-2 align-items-end my-3">
            <div class="form-group col-7 col-md-4">
                <label class="form-label">年度</label>
                <form-select
                    v-model="condition.year"
                    :options="storeSchoolYears()"
                    empty_option="-- 選択 --"
                    :disabled="condition_disabled"
                    @change="setYear"
                ></form-select>
            </div>
            <div class="form-group col-7 col-md-4">
                <label class="form-label">学年</label>
                <select v-model="condition.grade" class="form-select" @change="setGrade" :disabled="condition_disabled">
                    <option :value="null" selected>-- 選択 --</option>
                    <template v-if="condition.year">
                        <template v-for="(grade, index) in storeSchoolYearGrades()[condition.year]" :key="index">
                            <option :value="grade.key">{{ grade.label }}</option>
                        </template>
                    </template>
                </select>
            </div>

            <div class="col text-end align-items-end" v-if="condition.year && condition.grade">
                <button-exec-view
                    text="年間指導計画ダウンロード"
                    icon="bi-download"
                    size=""
                    class="me-3"
                    :disabled="planned_subjects.length === 0"
                    @click="downloadAnnualTeachingPlan()"
                ></button-exec-view>
                <button-go-edit
                    text="題材登録"
                    :linkTo="{ name: is_muji ? 'PageRepocraftMujiSubjectAdd' : 'PageRepocraftSubjectAdd', params: {year: condition.year, grade: condition.grade} }"
                    icon="bi-plus"
                ></button-go-edit>
            </div>

        </div>

        <hr>

        <inline-loader v-if="page_loading"></inline-loader>

        <div v-else>
            <div v-if="page_view && Object.keys(terms).length > 0">
                <template v-for="(term, key, index) in terms" :key="index">
                    <section-title
                        :title="`${terms[key]}【 合計実施コマ数：${sumPlannedLessonHoursPerTerm(planned_subjects.filter((s) => (s.term === Number(key))))} 】`"
                    >
                    </section-title>
                    <!-- 該当学期の題材が存在すれば -->
                    <template v-if="planned_subjects.filter((s) => (s.term === Number(key))).length">
                        <table-normal>
                            <colgroup>
                                <col class="col-13">
                                <col class="col-4">
                                <col class="col-2" v-if="false === is_muji">
                                <col class="col-2">
                                <col class="col-3">
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>題材名</th>
                                    <th>開始時期</th>
                                    <th v-if="false === is_muji">推奨<br>コマ数</th>
                                    <th>実施<br>コマ数</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                <template
                                    v-for="subject in planned_subjects.filter((s) => (s.term === Number(key)))"
                                    :key="subject.planned_subject_id"
                                >
                                    <tr v-if="Number(key) === subject.term">
                                        <td>{{ subject.subject_name }}</td>
                                        <td class="text-center">{{ $helper.dmp(subject.lesson_from) }}</td>
                                        <td class="text-center" v-if="false === is_muji">{{ subject.template_lesson_hours }}</td>
                                        <td class="text-center">{{ subject.planned_lesson_hours }}</td>
                                        <td class="text-center" v-if="condition.year && condition.grade">
                                            <button-go-edit text="設定" :linkTo="{ name: is_muji ? 'PageRepocraftMujiSubjectEdit' : 'PageRepocraftSubjectEdit', params: {year: condition.year, grade: condition.grade, subject_id: subject.planned_subject_id} }" icon="bi-chevron-right"></button-go-edit>
                                        </td>
                                    </tr>
                                </template>
                            </tbody>
                        </table-normal>
                    </template>
                    <alert-no-record text="実施題材が登録されていません。" v-else></alert-no-record>
                </template>
            </div>
            <div v-else>
                <alert-no-record>ご契約がありません。</alert-no-record>
            </div>
        </div>
    </template>

</template>

<script>
import { downloadBlob } from '@/shared/utilities/download';
import School from '@/shared/models/entities/school';
import PlannedSubject from '@/shared/models/entities/repocraft/planned-subject';
import Term from '@/shared/models/enums/term';
import PageTitle from '@/shared/components/design/PageTitle';
import SectionTitle from '@/shared/components/design/SectionTitle';
import AlertNoRecord from '@/shared/components/design/AlertNoRecord';
import ButtonExecView from '@/shared/components/design/buttons/ButtonExecView';
import ButtonGoEdit from '@/shared/components/design/buttons/ButtonGoEdit';
import FormSelect from '@/shared/components/form/FormSelect';
import TableNormal from '@/shared/components/design/TableNormal';
import InlineLoader from '@/shared/components/tool/InlineLoader';

export default {
    name: 'PageRepocraftSubjectList',
    components: {
        PageTitle,
        SectionTitle,
        AlertNoRecord,
        ButtonExecView,
        ButtonGoEdit,
        FormSelect,
        TableNormal,
        InlineLoader
    },
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage',
    ],
    data() {
        return {
            is_muji: this.$route.meta?.is_muji ?? false, // レポクラフト無地判定
            app_key: this.$route.meta?.is_muji ? 'repocraft_muji' : 'repocraft', //アプリ名取得

            condition: {
                year: null,
                grade: null,
            },

            school: new School(this.$store.state.school.auth.school),
            terms: {},
            planned_subjects: [],

            loading: true,
            page_loading: true,
            page_view: false,

            condition_disabled: false,
        }
    },
    computed: {
        //セッションから取得（レポクラフト/レポクラフト無地 切り替えを動的に）
        storeSchoolYearGrades() {
            return () => this.$store.state.school[this.app_key + '_year_grade'].year_grades;
        },
        storeSchoolYears() {
            return () => this.$store.state.school[this.app_key + '_year_grade'].years;
        },
        storeSchoolYear() {
            return () => this.$store.state.school[this.app_key + '_year_grade'].year;
        },
        storeSchoolGrade() {
            return () => this.$store.state.school[this.app_key + '_year_grade'].grade;
        },
    },
    mounted() {
        //年度・学年の選択肢取得（ライセンス保有の年度・学年）
        this.$store.dispatch(`school/${this.app_key}_year_grade/makeOptions`)
        .then(() => {
            //sessionから補完
            this.condition.year = this.storeSchoolYear();
            this.condition.grade = this.storeSchoolGrade();

            //学期情報を取得
            this.terms = Term.valuesFor(this.school.term_pattern);
        })
        .finally(() => {
            this.loading = false;
            this.setGrade();
        });
    },
    methods: {
        sumPlannedLessonHoursPerTerm(planned_subjects) {
            let sum = 0
            for (let subject of planned_subjects) {
                sum += subject.planned_lesson_hours;
            }
            return sum;
        },
        setYear() {
            this.condition_disabled = true; //selectをdisabled
            this.condition.grade = null; //gradeリセット
            this.page_view = false; //ページを非表示

            //session保存
            this.$store.commit(`school/${this.app_key}_year_grade/setYear`, this.condition.year);
            this.$store.commit(`school/${this.app_key}_year_grade/setGrade`, null);

            this.condition_disabled = false;
        },
        setGrade() {
            this.page_loading = true;
            this.condition_disabled = true; //selectをdisabled
            this.page_view = false; //ページを非表示
            if (this.$helper.isNud(this.condition.grade)) {
                //session保存
                this.$store.commit(`school/${this.app_key}_year_grade/setGrade`, null);
                this.condition_disabled = false;
                this.page_loading = false;
            } else {
                //session保存
                this.$store.commit(`school/${this.app_key}_year_grade/setGrade`, this.condition.grade);
                this.fetchPlannedSubjects();
            }
        },
        fetchPlannedSubjects() {
            this.page_loading = true;
            //題材一覧を取得
            this.planned_subjects = [];
            this.$http.get(`/school/${this.app_key}/year/${this.condition.year}/grade/${this.condition.grade}/subject`)
            .then(response => {
                for (let subject of response.data) {
                    this.planned_subjects.push(new PlannedSubject(subject));
                }
            })
            .finally(() => {
                this.page_view = true; //ページを表示
                this.condition_disabled = false;
                this.page_loading = false;
            });
        },
        //年間指導計画をダウンロードする
        downloadAnnualTeachingPlan() {
            this.startScreenLoading();

            this.$http.get(`/school/${this.app_key}/year/${this.condition.year}/grade/${this.condition.grade}/subject/download`, {responseType: 'arraybuffer'})
            .then(response => {
                let blob = new Blob([response.data], { type: 'application/pdf' });
                downloadBlob(blob, '年間指導計画.pdf');

            })
            .finally(() => {
                this.endScreenLoading();
            });
        }
    },
}
</script>

<style>
    .title-sub-1 {border-color: #198754 !important;}
</style>
